import { parseWithZod } from "@conform-to/zod";
import { ActionFunctionArgs } from "react-router-dom";
import { createToast } from "vercel-toast";
import { z } from "zod";
import { licenseFeeService } from "@/services/license-fee-service";
import { parseMoneyValue } from "@/utils/parse-money";
import { extractErrorMessage } from "@/utils/extract-error-message";

export const schema = z.object({
  companyId: z.string({
    required_error: "Selecione uma empresa através de seu nome.",
  }),
  packageId: z.string({
    required_error: "Selecione um pacote para criar o contrato.",
  }),
  startDate: z
    .string({ required_error: "Insira uma data de início." })
    .refine((date) => !isNaN(Date.parse(date)), {
      message: "Insira uma data válida.",
    }),
  endDate: z
    .string({ required_error: "Insira uma data de fim." })
    .refine((date) => !isNaN(Date.parse(date)), {
      message: "Insira uma data válida.",
    }),
  tier: z.string().optional(),
  members: z
    .number()
    .min(0, "Quantidade de membros não pode ser negativa.")
    .optional(),
  notes: z.string().optional(),
  totalAmount: z.string({ required_error: "Insira o valor total." }),
  exemptionAmount: z.string({
    required_error: "Insira o valor de isenção total.",
  }),
  installments: z
    .number({ required_error: "Insira o número de parcelas." })
    .min(0, "Insira o número de parcelas."),
  exceededPaymentDeadline: z
    .number({ required_error: "Insira o prazo em dias para excedente" })
    .min(0, "Insira o prazo em dias para excedente."),
  providerId: z.string({ required_error: "Selecione um provedor." }),
  paymentMethodId: z.string({
    required_error: "Selecione um método de pagamento.",
  }),
  setupFee: z.string().optional(),
  dealId: z.string().optional(),
  installmentsDates: z.array(
    z.string({
      required_error:
        "Uma ou mais datas são inválidas. Por favor, verifique novamente antes de registrar o license fee.",
    }),
  ),
  installmentsCompetence: z.array(
    z.string({
      required_error:
        "Uma ou mais competências são inválidas. Por favor, verifique novamente antes de registrar o license fee.",
    }),
  ),
  installmentsAmounts: z.array(
    z.string({
      required_error:
        "Uma ou mais parcelas são inválidas. Por favor, verifique novamente antes de registrar o license fee.",
    }),
  ),
  installmentsInvoiceCompetence: z
    .array(
      z
        .string({
          required_error:
            "Uma ou mais datas de competência são inválidas. Por favor, verifique novamente antes de registrar o license fee.",
        })
        .min(
          1,
          "A competência de faturamento é obrigatória em todas as parcelas.",
        ),
    )
    .min(1, "A competência de faturamento é obrigatória."),
  installmentsData: z.string().optional(),
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, { schema });

  if (submission.status !== "success") {
    return {
      submission: submission.reply(),
    };
  }

  const { value } = submission;
  const {
    startDate,
    endDate,
    totalAmount,
    installmentsDates,
    installmentsAmounts,
    setupFee,
    installmentsCompetence,
    installmentsInvoiceCompetence,
  } = value;
  const startDateObject = new Date(startDate);
  const endDateObject = new Date(endDate);

  if (startDateObject > endDateObject) {
    return {
      submission: submission.reply({
        fieldErrors: {
          endDate: ["A data de fim não pode ser anterior à data de início"],
        },
      }),
    };
  }

  const amountNumber = parseMoneyValue(totalAmount);
  // The setup fee renders date/competency inputs but no amount input, so it
  // leads the date/competency arrays as index 0 and makes them one longer than
  // installmentsAmounts. Offset the installment reads past that leading setup
  // fee entry so each license-fee installment lines up with its own values.
  const setupFeeOffset =
    installmentsDates.length > installmentsAmounts.length ? 1 : 0;

  const installments = installmentsAmounts.map(
    (installment: any, index: number) => ({
      value: parseMoneyValue(installment),
      paymentDate: installmentsDates[index + setupFeeOffset],
      competencyDate: installmentsCompetence[index + setupFeeOffset],
      invoicingCompetencyDate:
        installmentsInvoiceCompetence[index + setupFeeOffset],
    }),
  );

  const hasMissingInvoiceCompetence = installments.some(
    (installment) => !installment.invoicingCompetencyDate,
  );

  if (hasMissingInvoiceCompetence) {
    return {
      submission: submission.reply({
        fieldErrors: {
          installmentsData: [
            "A competência de faturamento é obrigatória em todas as parcelas.",
          ],
        },
      }),
    };
  }

  let setupFeeData = {};

  if (setupFee) {
    setupFeeData = {
      value: parseMoneyValue(setupFee),
      paymentDate: installmentsDates[0],
      competencyDate: installmentsCompetence[0],
      invoicingCompetencyDate: installmentsInvoiceCompetence[0],
    };
  }

  const hasInvalidInstallment = installments.some(
    (installment) => installment.value <= 0,
  );

  if (hasInvalidInstallment) {
    return {
      submission: submission.reply({
        fieldErrors: {
          installmentsData: [
            "Os valores das parcelas não podem ser zero ou negativos.",
          ],
        },
      }),
    };
  }

  const installmentsAmountTotal = installments.reduce(
    (acc: number, curr: any) => acc + curr.value,
    0,
  );
  const formattedTotal = Number(installmentsAmountTotal.toFixed(2));

  if (formattedTotal !== amountNumber) {
    return {
      submission: submission.reply({
        fieldErrors: {
          installmentsData: [
            "A soma dos valores das parcelas não corresponde ao valor total.",
          ],
        },
      }),
    };
  }

  const licenseFeeData = {
    companyId: value.companyId,
    packageId: value.packageId,
    startDate: value.startDate,
    endDate: value.endDate,
    tier: value.tier,
    members: value.members,
    notes: value.notes,
    amount: amountNumber,
    exemptionAmount: parseMoneyValue(value.exemptionAmount),
    installments: installments,
    paymentProvider: value.providerId,
    paymentMethod: value.paymentMethodId,
    setupFee: setupFee ? setupFeeData : null,
    dealId: value.dealId,
  };

  try {
    await licenseFeeService.createLicenseFee(licenseFeeData);

    createToast("License fee criado com sucesso", {
      type: "success",
      timeout: 4000,
    });
  } catch (e) {
    console.error(e);
    createToast(`${extractErrorMessage(e, "Erro ao criar license fee")}`, {
      type: "error",
      timeout: 4000,
    });
    return {
      values: value,
    };
  }

  return {
    submission: submission.reply({ resetForm: true }),
  };
}

export type Action = Awaited<ReturnType<typeof action>>;
